import { HEADERS, OSP_REG_BRAND } from "./constants";
import { getEnvIndexBaseUrl } from "../api/osp/constants";

export interface ApiTokenResponse {
  access_token: string;
  refresh_token: string;
}

export interface ApiIdTokenResponse {
  id_token: string;
}

export class AuthApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly body: string,
  ) {
    super(`Auth API ${status}`);
    this.name = "AuthApiError";
  }

  get isAuthFailure(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isServerError(): boolean {
    return this.status >= 500;
  }
}

function baseUrl(): string {
  return getEnvIndexBaseUrl(process.env.NEXT_PUBLIC_ENV ?? "dev");
}

function appId(): string {
  return process.env.NEXT_PUBLIC_APP_ID ?? "100";
}

interface OspEnvelope<T> {
  code: number;
  msg?: string;
  data?: T;
}

async function assertOk<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const body = await response.text().catch(() => "");
    throw new AuthApiError(response.status, body);
  }
  const envelope: OspEnvelope<T> = await response.json();
  if (envelope.code !== 0) {
    throw new AuthApiError(envelope.code, envelope.msg ?? "");
  }
  return envelope.data as T;
}

export const authApi = {
  /** 登录 — 社交: headers 含 OS-Firebase-Token；邮箱: OS-Email + OS-Code */
  async login(authHeaders: Record<string, string>): Promise<ApiTokenResponse> {
    const response = await fetch(`${baseUrl()}/auth/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        [HEADERS.APP_ID]: appId(),
        [HEADERS.REG_BRAND]: OSP_REG_BRAND,
        ...authHeaders,
      },
      body: JSON.stringify({ eoa_address: "", signature: "" }),
    });
    return assertOk<ApiTokenResponse>(response);
  },

  /** 刷新令牌（当前：URL query；后续优化：POST body） */
  async refresh(refreshToken: string): Promise<ApiTokenResponse> {
    const query = new URLSearchParams({ refresh_token: refreshToken });
    const response = await fetch(`${baseUrl()}/auth/refresh?${query}`, {
      method: "POST",
      headers: {
        [HEADERS.APP_ID]: appId(),
        [HEADERS.REG_BRAND]: OSP_REG_BRAND,
      },
    });
    return assertOk<ApiTokenResponse>(response);
  },

  /** 获取 id_token */
  async getIdToken(accessToken: string): Promise<ApiIdTokenResponse> {
    const response = await fetch(`${baseUrl()}/auth/id_token`, {
      headers: {
        [HEADERS.APP_ID]: appId(),
        [HEADERS.REG_BRAND]: OSP_REG_BRAND,
        authorization: `Bearer ${accessToken}`,
      },
    });
    return assertOk<ApiIdTokenResponse>(response);
  },
};
